import React from "react";
import styled from "@emotion/styled";
import { IoIosAdd, IoIosRemove } from "react-icons/io";
import { ThemeContext, UserContext, LanguageContext } from "../contexts/Context";
import { lightTint, readableText } from "../utils/themeContrast";
import promo from "./customerComponents/Promotion";

const CheckoutPanel = ({ index, cart, item, deleteActive, handleDelete, handleAdd, handleMinus, handleItemClick, lanIndex, isLanReverse, currencySymbol, hidePrice, receipt = false }) => {
  const theme = React.useContext(ThemeContext);
  const { tax, displayNetAmount } = React.useContext(UserContext);
  const language = React.useContext(LanguageContext);
  if (typeof lanIndex === "undefined") return null;
  const titleOf = option => (lanIndex == 0 && !isLanReverse) || (lanIndex == 1 && isLanReverse) ? option.title : (option.title2 || option.title);
  const descriptions = groups => (groups || []).filter(group => group.optionActive === true).flatMap(group =>
    (group.options || []).filter(option => option.active === true).flatMap(option => [
      `${option.qty > 1 ? option.qty + " x " : ""}${titleOf(option)}`,
      ...descriptions(option.optionArray)
    ])
  );
  const multiplier = displayNetAmount ? 1 + Number(tax.taxMode == 0 ? tax.tax : item.tax || 0) : 1;
  const price = promo.subTotalAmount([item]);
  const original = promo.subTotalDefaultAmount([item]);
  const details = descriptions(item.optionArray).join(" \u00b7 ");
  return <Panel themeContext={theme} receipt={receipt}>
    {!receipt && item.image && <Thumbnail src={item.image} alt="" />}
    <Information>
      <Title>{!receipt && !deleteActive && `${item.qty} x `}{titleOf(item)}</Title>
      {details && <Description themeContext={theme}>{details}</Description>}
      {item.hasRemark && item.remark && <Description themeContext={theme}>{language[lanIndex].remark}: {item.remark}</Description>}
      {item.isTakeAway && <Description themeContext={theme}>{language[lanIndex].takeAway}</Description>}
      {deleteActive && <Actions>
        {handleItemClick && <Action type="button" onClick={handleItemClick(item, index)}>{language[lanIndex].edit || "Edit"}</Action>}
        {handleItemClick && handleDelete && <span aria-hidden="true">|</span>}
        {handleDelete && <Action type="button" remove onClick={() => handleDelete(cart, index)}>{lanIndex == 1 ? "\u79fb\u9664" : "Remove"}</Action>}
      </Actions>}
    </Information>
    <PriceColumn>
      {!hidePrice && <>
        {original !== price && <OriginalPrice>{currencySymbol}{(original * multiplier).toFixed(2)}</OriginalPrice>}
        <span>{currencySymbol}{(price * multiplier).toFixed(2)}</span>
      </>}
      {receipt && <ReceiptQty>{lanIndex == 1 ? "??" : "Qty"} {item.qty}</ReceiptQty>}
      {deleteActive && <Quantity themeContext={theme}>
        <QtyButton type="button" aria-label={`Decrease ${titleOf(item)} quantity`} onClick={() => handleMinus(cart, index)}><IoIosRemove size={16} /></QtyButton>
        <span>{item.qty}</span>
        <QtyButton type="button" aria-label={`Increase ${titleOf(item)} quantity`} onClick={() => handleAdd(cart, index)}><IoIosAdd size={16} /></QtyButton>
      </Quantity>}
    </PriceColumn>
  </Panel>;
};
export default CheckoutPanel;

const Panel = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  box-sizing: border-box;
  width: calc(100% - 28px);
  max-width: 522px;
  margin: 0 auto;
  padding: 14px 0;
  border-bottom: 1px solid ${props => lightTint(props.themeContext.primaryColor, 0.2)};
  color: ${props => readableText(props.themeContext.backgroundColor, props.themeContext.fontPrimary)};
  font-family: ${props => props.themeContext.fontFamily};
  ${props => props.receipt && `width: 100%; max-width: none; padding: 10px 0; gap: 10px; border-bottom: 1px solid rgba(128,128,128,.1); &:last-child { border-bottom: 0; }`}
`;
const ReceiptQty = styled.div`font-size: 12px; opacity: .7; margin-top: 5px; font-weight: 400;`;
const Thumbnail = styled.img`
  width: 48px;
  height: 48px;
  flex: 0 0 48px;
  border-radius: 9px;
  object-fit: cover;
`;
const Information = styled.div`
  flex: 1;
  min-width: 0;
  text-align: left;
`;
const Title = styled.div`
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
  overflow-wrap: anywhere;
`;
const Description = styled.div`
  margin-top: 4px;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.45;
  color: ${props => readableText(props.themeContext.backgroundColor, props.themeContext.descriptionColor, props.themeContext.fontPrimary)};
`;
const Actions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  margin-top: 6px;
  font-size: 12px;
  > span { opacity: 0.3; }
`;
const Action = styled.button`
  border: 0;
  padding: 2px 0;
  background: transparent;
  color: ${props => props.remove ? "#c62828" : "inherit"};
  font: inherit;
  cursor: pointer;
`;
const PriceColumn = styled.div`
  display: flex;
  flex-shrink: 0;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
  white-space: nowrap;
`;
const OriginalPrice = styled.span`
  font-size: 12px;
  text-decoration: line-through;
`;
const Quantity = styled.div`
  display: grid;
  grid-template-columns: 24px minmax(20px, auto) 24px;
  height: 32px;
  align-items: center;
  border: 1px solid ${props => lightTint(props.themeContext.primaryColor, 0.2)};
  border-radius: 999px;
  color: ${props => readableText(lightTint(props.themeContext.primaryColor), props.themeContext.primaryColor)};
  background: ${props => lightTint(props.themeContext.primaryColor)};
  > span { text-align: center; }
`;
const QtyButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
`;
